/**
 * LeftLeafSVG Component
 *
 * A customizable SVG icon component that renders a left-facing leaf illustration.
 *
 * @example
 * // Basic usage with default props (19x20, color #992E00)
 * <LeftLeafSVG />
 *
 * @example
 * // Custom size and color
 * <LeftLeafSVG width={24} height={26} fill="#FF5733" />
 *
 * @example
 * // Using size prop for equal width and height
 * <LeftLeafSVG size={20} fill="#00FF00" />
 *
 * @example
 * // With className for additional styling
 * <LeftLeafSVG
 *   width={30}
 *   height={32}
 *   fill="currentColor"
 *   className="hover:opacity-80"
 * />
 *
 * @param {Object} props - Component props
 * @param {number} [props.width=19] - Width of the SVG in pixels
 * @param {number} [props.height=20] - Height of the SVG in pixels
 * @param {number} [props.size] - Optional single size value for both width and height (takes precedence over width/height)
 * @param {string} [props.fill='#992E00'] - Fill color for the SVG paths. Can be a hex color, rgb, or CSS color name
 * @param {string} [props.color] - Alias for fill prop (color takes precedence over fill if both are provided)
 * @param {string} [props.className] - Additional CSS classes to apply to the SVG element
 */
interface LeftLeafSVGProps {
  width?: number
  height?: number
  size?: number
  fill?: string
  color?: string
  className?: string
}

export default function LeftLeafSVG({
  width = 19,
  height = 20,
  size,
  fill = '#992E00',
  color,
  className
}: LeftLeafSVGProps) {
  // If size is provided, use it for both width and height
  const finalWidth = size ?? width
  const finalHeight = size ?? height
  // color prop takes precedence over fill
  const finalFill = color ?? fill

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={finalWidth}
      height={finalHeight}
      viewBox='0 0 19 20'
      fill='none'
      className={className}
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M9.04246 1.85483C9.37664 1.32844 9.21515 0.634372 8.68174 0.304569C8.14834 -0.0252164 7.44502 0.134152 7.11082 0.660538L6.62327 1.4285C6.28907 1.95488 6.45057 2.64895 6.98399 2.97874C7.51739 3.30854 8.22072 3.14917 8.5549 2.62278L9.04246 1.85483ZM2.9713 3.18039C3.4283 2.74796 4.15004 2.75854 4.58335 3.20402L5.67264 4.32388L7.50393 3.82334C8.11262 3.65697 8.73707 4.007 8.89869 4.60515C9.06033 5.20328 8.69794 5.82305 8.08926 5.98942L5.60366 6.66878C5.20033 6.77904 4.7707 6.66394 4.48355 6.36874L2.92845 4.77001C2.49513 4.32453 2.51432 3.61284 2.9713 3.18039ZM2.80357 7.01196C2.32732 6.61143 1.60619 6.67046 1.19289 7.14379C0.7796 7.61711 0.830641 8.3255 1.3069 8.72603L3.50439 10.5741C3.83969 10.856 4.31326 10.9193 4.71858 10.7362L7.37496 9.53647C7.95065 9.27646 8.20879 8.60936 7.95151 8.04646C7.69422 7.48358 7.01895 7.23805 6.44323 7.49807L4.45136 8.3977L2.80357 7.01196ZM7.37658 13.3896C7.9212 13.0762 8.10651 12.3876 7.79047 11.8516C7.47443 11.3155 6.77672 11.135 6.23209 11.4484L4.4623 12.4667L2.27343 11.595C1.69021 11.3628 1.02497 11.6413 0.787565 12.2172C0.550159 12.793 0.830492 13.4481 1.41371 13.6804L4.12077 14.7584C4.44762 14.8886 4.81766 14.862 5.12288 14.6863L7.37658 13.3896Z'
        fill={finalFill}
      />
      <path
        d='M10.2254 14.504L9.43558 15.9958C10.2538 16.0601 12.3894 16.5409 14.3858 17.9493C14.8783 18.3358 14.96 19.0434 14.5683 19.5297C14.1764 20.0157 13.4594 20.0963 12.9668 19.7098C12.6372 19.3574 11.381 18.537 8.99362 18.0747C8.59961 18.0236 8.23509 18.0611 7.88374 18.0747L7.74194 18.0611C7.72586 18.0623 7.7097 18.0632 7.69346 18.0638L3.9429 18.203C3.30743 18.2266 2.76043 17.7485 2.72115 17.135C2.68185 16.5214 3.16512 16.005 3.80059 15.9814L6.89093 15.8666L8.16685 13.4568C8.45358 12.9152 9.14684 12.7106 9.71529 12.9998C10.2837 13.289 10.5121 13.9625 10.2254 14.504Z'
        fill={finalFill}
      />
    </svg>
  )
}
